import type { MatchHandicap, MatchHandicapSide, MatchView } from '@pingo/shared';

export interface BracketGroup {
  bracketId: string;
  rounds: { bracketRound: number; matches: MatchView[] }[];
}

/** Matches grouped into brackets (columns = bracketRound, sorted by bracketIndex). */
export function groupBrackets(matches: MatchView[]): BracketGroup[] {
  const byBracket = new Map<string, Map<number, MatchView[]>>();
  for (const m of matches) {
    if (!m.bracketId) continue;
    const rounds = byBracket.get(m.bracketId) ?? new Map<number, MatchView[]>();
    const r = m.bracketRound ?? 1;
    rounds.set(r, [...(rounds.get(r) ?? []), m]);
    byBracket.set(m.bracketId, rounds);
  }
  return [...byBracket.entries()].map(([bracketId, rounds]) => ({
    bracketId,
    rounds: [...rounds.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([bracketRound, ms]) => ({
        bracketRound,
        matches: [...ms].sort((a, b) => (a.bracketIndex ?? 0) - (b.bracketIndex ?? 0)),
      })),
  }));
}

/** Matches outside a bracket (e.g. team match play), grouped by round number. */
export function groupLooseMatches(
  matches: MatchView[],
): { roundNo: number | null; matches: MatchView[] }[] {
  const map = new Map<number | null, MatchView[]>();
  for (const m of matches) {
    if (m.bracketId) continue;
    map.set(m.roundNo, [...(map.get(m.roundNo) ?? []), m]);
  }
  return [...map.entries()]
    .sort((a, b) => (a[0] ?? 0) - (b[0] ?? 0))
    .map(([roundNo, ms]) => ({ roundNo, matches: ms }));
}

/**
 * Total rounds of a bracket, derived from its shape rather than from the highest round present:
 * later-round matches only exist once a winner advances, so an 8-player bracket starts with just
 * its four round-1 matches. Round 1 always holds every slot (byes included), so size = 2 × its
 * match count; a round r with 0-based slot index i also implies at least r + ⌈log2(i + 1)⌉ rounds.
 */
export function bracketTotalRounds(rounds: BracketGroup['rounds']): number {
  let total = 1;
  for (const r of rounds) {
    total = Math.max(total, r.bracketRound);
    const maxIdx = Math.max(0, ...r.matches.map((m) => m.bracketIndex ?? 0));
    total = Math.max(total, r.bracketRound + Math.ceil(Math.log2(maxIdx + 1)));
    if (r.bracketRound === 1 && r.matches.length > 0) {
      total = Math.max(total, 1 + Math.ceil(Math.log2(r.matches.length)));
    }
  }
  return total;
}

/** Every round 1..total, with [] for rounds whose matches do not exist yet (placeholder columns). */
export function bracketColumns(
  rounds: BracketGroup['rounds'],
): { bracketRound: number; matches: MatchView[] }[] {
  const total = bracketTotalRounds(rounds);
  return Array.from({ length: total }, (_, i) => ({
    bracketRound: i + 1,
    matches: rounds.find((r) => r.bracketRound === i + 1)?.matches ?? [],
  }));
}

/**
 * Bracket round label key: last round = final, then semi, quarter, else "round of N".
 * Returns [i18n key, params].
 */
export function bracketRoundLabel(
  round: number,
  lastRound: number,
): [string, Record<string, number>] {
  const fromEnd = lastRound - round;
  if (fromEnd === 0) return ['matches.final', {}];
  if (fromEnd === 1) return ['matches.semifinal', {}];
  if (fromEnd === 2) return ['matches.quarterfinal', {}];
  return ['matches.roundOf', { n: 2 ** (fromEnd + 1) }];
}

/** Regulation holes of a match (its hole list), for "all square after 18 · extra holes". */
export function regulationHoles(m: Pick<MatchView, 'holes'>): number {
  return m.holes.length || 18;
}

/** Card hole numbers of the match round in card order (falls back to the stroke holes). */
export function cardHoles(m: Pick<MatchView, 'holes'>, side?: MatchHandicapSide): number[] {
  const holes = m.holes.length
    ? m.holes.map((h) => h.hole)
    : (side?.holes ?? []).map((h) => h.hole);
  return [...new Set(holes)].sort((a, b) => a - b);
}

/** Who receives the strokes of a side: the player for singles, the side (team) name otherwise. */
export function sideLabel(m: MatchView, h: MatchHandicap, key: 'A' | 'B'): string {
  const side = h[key];
  if (side.players.length === 1) return side.players[0]!.name;
  return (key === 'A' ? m.sideA : m.sideB).name;
}

/** `?tv=1` on the public matches page: the clubhouse TV board (dark, full screen). */
export function isTvSearch(search: unknown): boolean {
  const tv = (search as { tv?: unknown } | null)?.tv;
  return tv === 1 || tv === '1' || tv === true || tv === 'true';
}

/**
 * Short calendar day of a bracket column, the demo's form: ko/ja/zh "10/3", vi "3/10", en (UK)
 * "3 Oct". `date` is a 'yyyy-MM-dd' calendar day (no timezone).
 */
export function shortRoundDate(date: string, locale: string): string {
  const [, mm, dd] = date.slice(0, 10).split('-');
  const m = Number(mm);
  const d = Number(dd);
  if (!m || !d) return date;
  const lng = locale.toLowerCase().slice(0, 2);
  if (lng === 'vi') return `${d}/${m}`;
  if (lng === 'ko' || lng === 'ja' || lng === 'zh') return `${m}/${d}`;
  const month = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' }).format(
    new Date(Date.UTC(2000, m - 1, 15)),
  );
  return `${d} ${month}`;
}

/** Date of a bracket column: the round (roundNo) of its first match that has one. */
export function columnRoundDate(
  matches: Pick<MatchView, 'roundNo'>[],
  rounds: ReadonlyArray<{ roundNo: number; date: string }> | undefined,
): string | null {
  for (const m of matches) {
    const r = m.roundNo !== null ? rounds?.find((x) => x.roundNo === m.roundNo) : undefined;
    if (r?.date) return r.date;
  }
  return null;
}

/** "8강 · 10/3" — column label plus the short round date when known. */
export function withRoundDate(label: string, date: string | null, locale: string): string {
  return date ? `${label} · ${shortRoundDate(date, locale)}` : label;
}

/**
 * Stroke-index range of a side's stroke holes when they are two or more holes in a row (SI from..to
 * without gaps); null otherwise (then the legend leaves the range out).
 */
export function strokeIndexRange(
  side: Pick<MatchHandicapSide, 'holes'>,
): { from: number; to: number } | null {
  const si = [...new Set(side.holes.map((h) => h.strokeIndex))].sort((a, b) => a - b);
  if (si.length < 2) return null;
  const from = si[0]!;
  const to = si[si.length - 1]!;
  return to - from + 1 === si.length ? { from, to } : null;
}

/** Most strokes the side receives on one hole (1 = the plain "+1" legend). */
export function maxHoleStrokes(side: Pick<MatchHandicapSide, 'holes'>): number {
  return Math.max(0, ...side.holes.map((h) => h.strokes));
}

/** Replace a match in a list by id (or append). */
export function upsertMatch(list: MatchView[] | undefined, match: MatchView): MatchView[] {
  const cur = list ?? [];
  const i = cur.findIndex((m) => m.matchId === match.matchId);
  if (i < 0) return [...cur, match];
  const next = [...cur];
  next[i] = match;
  return next;
}
